'use client';

import { c as _c } from "react/compiler-runtime";
import * as Headless from '@headlessui/react';
import type React from 'react';
import { forwardRef, useEffect, useRef } from 'react';
import { cn } from '../../../shared/cn';
import { Button } from '../Button';
type DropdownButtonProps = {
  className?: string;
  as?: React.ElementType;
  /** Headless Menu refocuses the button on close without preventScroll; use in scroll containers. */
  preventScrollOnFocus?: boolean;
} & Omit<Headless.MenuButtonProps<typeof Button>, 'className' | 'as'>;
function patchFocusWithoutScroll(node: HTMLButtonElement): () => void {
  const originalFocus = node.focus.bind(node);
  node.focus = (options?: FocusOptions) => {
    originalFocus({
      ...options,
      preventScroll: true
    });
  };
  return () => {
    node.focus = originalFocus;
  };
}
export const DropdownButton = forwardRef(function DropdownButton(t0, ref) {
  const $ = _c(17);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      as: t1,
      className,
      preventScrollOnFocus: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const as = t1 === undefined ? Button : t1;
  const preventScrollOnFocus = t2 === undefined ? false : t2;
  const cleanupRef = useRef(null);
  let t3;
  if ($[5] !== preventScrollOnFocus || $[6] !== ref) {
    t3 = node => {
      cleanupRef.current?.();
      cleanupRef.current = preventScrollOnFocus && node != null ? patchFocusWithoutScroll(node) : null;
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          ref.current = node;
        }
      }
    };
    $[5] = preventScrollOnFocus;
    $[6] = ref;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const setRefs = t3;
  let t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      cleanupRef.current?.();
    };
    t5 = [];
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== className) {
    t6 = cn("data-active:ring-2 data-active:ring-algonavy-400! data-active:ring-outline", className);
    $[10] = className;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== as || $[13] !== props || $[14] !== setRefs || $[15] !== t6) {
    t7 = <Headless.MenuButton as={as} {...props} ref={setRefs} className={t6} />;
    $[12] = as;
    $[13] = props;
    $[14] = setRefs;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
});